import type { AvailabilityBlock } from "@store/disponibiliteStore";
import { getBlockRecurrence } from "@store/disponibiliteStore";
import { GRID_HOUR_START, GRID_HOUR_END } from "@utils/timeGrid";

/**
 * Multi-day / all-day / recurring block helpers.
 *
 * A block now behaves like a Google-Calendar event: it spans [date … endDate]
 * (inclusive), can be `allDay`, and recurs `none | weekly | annual`. Every grid
 * renders blocks by asking, per visible day, "what slice of this block lands
 * here?" via {@link blockSegmentOnDate} — which centralises all the span/
 * recurrence logic so the grids stay simple.
 */

/** Day-of-week 0=Mon … 6=Sun from an ISO date. */
export function dowIndex(dateStr: string): number {
  const jsDay = new Date(dateStr + "T00:00:00").getDay();
  return jsDay === 0 ? 6 : jsDay - 1;
}

/** "MM-DD" of an ISO date (for annual recurrence matching). */
function monthDay(dateStr: string): string {
  return dateStr.slice(5, 10);
}

/** End date (inclusive) of a block — falls back to its start date. */
export function blockEndDate(b: Pick<AvailabilityBlock, "date" | "endDate">): string {
  return b.endDate && b.endDate >= b.date ? b.endDate : b.date;
}

/** End hour on the block's last day. */
function blockEndHour(b: Pick<AvailabilityBlock, "startHour" | "durationHours" | "endHour">): number {
  return b.endHour ?? b.startHour + b.durationHours;
}

export interface DaySegment {
  startHour: number;
  endHour: number;
  /** True on the block's first covered day (for labels/handles). */
  isFirstDay: boolean;
  /** True on the block's last covered day. */
  isLastDay: boolean;
}

/**
 * The slice of `block` that falls on ISO `date`, or null if it doesn't cover it.
 * `gridStart`/`gridEnd` bound all-day and middle-of-span days.
 */
export function blockSegmentOnDate(
  block: AvailabilityBlock,
  date: string,
  gridStart: number = GRID_HOUR_START,
  gridEnd: number = GRID_HOUR_END,
): DaySegment | null {
  const rec = getBlockRecurrence(block);

  // Weekly recurrence stays a single-day pattern keyed on dayOfWeek, repeated only
  // inside its « Du … Au » window (open end = forever) — same rule as the back
  // (booking_controller._block_covers_date).
  if (rec === "weekly") {
    if (block.dayOfWeek === undefined || block.dayOfWeek !== dowIndex(date)) return null;
    if (block.date && date < block.date) return null;
    if (block.endDate && block.endDate >= (block.date || "") && date > block.endDate) return null;
    // Older weekly blocks with an « Au » were loaded as multi-day spans (duration 0,
    // endHour set): the slot's end is then `endHour`.
    const endHour = block.durationHours > 0 ? block.startHour + block.durationHours : blockEndHour(block);
    return block.allDay
      ? { startHour: gridStart, endHour: gridEnd, isFirstDay: true, isLastDay: true }
      : { startHour: block.startHour, endHour, isFirstDay: true, isLastDay: true };
  }

  const start = block.date;
  const end = blockEndDate(block);
  let covers: boolean;
  let isFirst: boolean;
  let isLast: boolean;

  if (rec === "annual") {
    const md = monthDay(date);
    const ms = monthDay(start);
    const me = monthDay(end);
    // Same-year range, with wrap-around support (e.g. 12-28 → 01-02).
    covers = ms <= me ? md >= ms && md <= me : md >= ms || md <= me;
    isFirst = md === ms;
    isLast = md === me;
  } else {
    covers = date >= start && date <= end;
    isFirst = date === start;
    isLast = date === end;
  }
  if (!covers) return null;

  if (block.allDay) return { startHour: gridStart, endHour: gridEnd, isFirstDay: isFirst, isLastDay: isLast };

  const sh = isFirst ? block.startHour : gridStart;
  const eh = isLast ? blockEndHour(block) : gridEnd;
  return { startHour: sh, endHour: Math.max(eh, sh), isFirstDay: isFirst, isLastDay: isLast };
}

/** True when the block covers `date` at all (any segment). */
export function blockCoversDate(block: AvailabilityBlock, date: string): boolean {
  return blockSegmentOnDate(block, date) !== null;
}

/**
 * Non-working ("off") hour ranges for a worker on `date`, i.e. the complement of
 * their productive zones within [gridStart, gridEnd].
 *
 * Productive = any block whose zoneType is NOT a break: `pause` and `conge` are
 * treated as off, exactly like a gap with no zone at all. This is what the grid
 * greys out and what a drop must flag — the lunch break is usually just a gap
 * between the morning and afternoon zones (no explicit `pause` block), and a
 * worker with no zone at all is off for the whole day.
 */
export function workerOffIntervals(
  blocks: AvailabilityBlock[],
  workerId: string,
  date: string,
  gridStart: number = GRID_HOUR_START,
  gridEnd: number = GRID_HOUR_END,
): [number, number][] {
  // Collect productive (workable) segments.
  const work: [number, number][] = [];
  for (const b of blocks) {
    if (b.workerId !== workerId) continue;
    if (b.zoneType === "pause" || b.zoneType === "conge") continue;
    const seg = blockSegmentOnDate(b, date, gridStart, gridEnd);
    if (seg) work.push([seg.startHour, seg.endHour]);
  }
  work.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const [s, e] of work) {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  }
  // Complement within the grid bounds.
  const off: [number, number][] = [];
  let cursor = gridStart;
  for (const [s, e] of merged) {
    if (s > cursor) off.push([cursor, s]);
    cursor = Math.max(cursor, e);
  }
  if (cursor < gridEnd) off.push([cursor, gridEnd]);
  return off;
}

/** First off-region for a worker that overlaps [startHour, endHour], or null. */
export function firstOverlappingOffRegion(
  blocks: AvailabilityBlock[],
  workerId: string,
  date: string,
  startHour: number,
  endHour: number,
): { start: number; end: number } | null {
  for (const [s, e] of workerOffIntervals(blocks, workerId, date)) {
    if (startHour < e && endHour > s) return { start: s, end: e };
  }
  return null;
}

/**
 * Why the worker is away that day, shown under the column header (Mélanie, 2026-10-08:
 * « que le type d'absence s'affiche sur le compagnon, comme la formation pour Matthis »).
 * Congé/formation blocks, plus an all-day pause carrying a comment.
 */
export function absenceLabel(blocks: AvailabilityBlock[], workerId: string, date: string): string | null {
  for (const b of blocks) {
    if (b.workerId !== workerId || !blockSegmentOnDate(b, date)) continue;
    const comment = (b.label || "").trim();
    if (b.zoneType === "conge") {
      const kind = b.absenceKind === "formation" ? "Formation" : "Congé";
      return comment && comment.toLowerCase() !== kind.toLowerCase() ? `${kind} · ${comment}` : kind;
    }
    if (b.zoneType === "pause" && b.allDay && comment) return comment;
  }
  return null;
}
